import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestClient, requestStatus } from 'src/lib/services/api/request-api';
import { requestQueryKey } from 'src/features/requests/request-query-key';
import { RequestQueryApiResponse } from 'src/features/requests/use-request-query-context';
import { reactQueryClient } from 'src/providers/ReactQueryClientProvider';
import { authQueries } from 'src/api/queries';

type RequestMutations = {
  approve: ReturnType<typeof useMutation<void, unknown, string, unknown>>;
  requestReedit: ReturnType<typeof useMutation<void, unknown, string, unknown>>;
  undoReeditRequest: ReturnType<typeof useMutation<RequestQueryApiResponse, any, string, unknown>>;
  complete: ReturnType<typeof useMutation<void, unknown, string, unknown>>;
  setQueryData: (
    request: string,
    updater: Parameters<typeof reactQueryClient.setQueryData<RequestQueryApiResponse>>[1],
  ) => void;
  isAnyMutationLoading: boolean;
};

export const useRequestMutations = (): RequestMutations => {
  const client = useQueryClient();

  const setQueryData = (
    request: string,
    updater: Parameters<typeof client.setQueryData<RequestQueryApiResponse>>[1],
  ) => client.setQueryData<RequestQueryApiResponse>(requestQueryKey.self(request), updater);

  // Outcomes are child requests; a status/flag change on one must also refresh
  // its parent project's detail query, because the multi-outcome Summary card
  // reads each outcome's status from the parent's `multiple_outcomes` snapshot
  // (#2517). Without this the Summary pill stays stale (e.g. "Queued") and
  // diverges from the Deliverables tab, whose separate outcomes query is
  // already invalidated elsewhere. No-op for non-outcome requests (no parent).
  const invalidateParentDetail = (request: string) => {
    const parentId = client.getQueryData<RequestQueryApiResponse>(
      requestQueryKey.self(request),
    )?.parent_id;
    if (parentId) {
      client.invalidateQueries({ queryKey: requestQueryKey.self(parentId) });
    }
  };

  const approve = useMutation({
    mutationFn: (request: string) => requestClient.approve(request),
    onMutate: async (request) => {
      setQueryData(request, (prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          approved_at: new Date().toISOString(),
        };
      });
    },
    onSuccess: async (response, request) => {
      client.invalidateQueries(requestQueryKey.self(request));
      // Approving must drop the row from the "What Needs My Attention"
      // dashboard widget (and the requests list / kanban) on the next paint.
      // Without invalidating the plural list + counts the card lingers as
      // Ready for Review until the 30s poll lands — Karina's #2447 followup.
      client.invalidateQueries({ queryKey: [authQueries.requests] });
      client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
    },
  });

  const requestReedit = useMutation({
    mutationFn: (request: string) => requestClient.requestReedit(request),
    onMutate: async (request) => {
      setQueryData(request, (prev) => {
        if (!prev) {
          return prev;
        }

        // Mirror backend: Request Re-edit flips delivered → queued and sets
        // the timestamp. Without flipping the status here, the pill stays on
        // Ready for Review for the optimistic window while the loader (gated
        // only on requested_reedit_at) already attaches — Karina's #2455
        // single-frame flash. Pairs with the reverse flip in undoReeditRequest.
        const reeditingStatus =
          prev.status === requestStatus.delivered ? requestStatus.queued : prev.status;

        return {
          ...prev,
          requested_reedit_at: new Date().toISOString(),
          status: reeditingStatus,
        };
      });
    },
    onSuccess: async (response, request) => {
      client.refetchQueries({
        queryKey: requestQueryKey.self(request),
      });
      // Same widget-staleness reason as approve.onSuccess above (#2447).
      client.invalidateQueries({ queryKey: [authQueries.requests] });
      client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
      invalidateParentDetail(request);
    },
  });

  const undoReeditRequest = useMutation({
    mutationFn: (request: string) => requestClient.undoReeditRequest(request),
    onMutate: async (request) => {
      setQueryData(request, (prev) => {
        if (!prev) {
          return prev;
        }

        // Mirror backend PR #407: Undo from a queued row reverts status
        // to delivered. Without flipping the status optimistically, the
        // pill briefly shows "Queued" before refetch lands the
        // server's delivered state — Karina's #2383 follow-up flagged
        // the ~2s flicker.
        const restoredStatus =
          prev.status === requestStatus.queued ? requestStatus.delivered : prev.status;

        return {
          ...prev,
          requested_reedit_at: undefined,
          status: restoredStatus,
        };
      });
    },
    onSuccess: async (response, request) => {
      client.refetchQueries({
        queryKey: requestQueryKey.self(request),
      });
      // Same widget-staleness reason as approve.onSuccess above (#2447).
      client.invalidateQueries({ queryKey: [authQueries.requests] });
      client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
      invalidateParentDetail(request);
    },
  });

  const complete = useMutation({
    mutationFn: (request: string) => requestClient.complete(request),
    onMutate: async (request) => {
      setQueryData(request, (prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          status: requestStatus.complete,
          status_changed_at: new Date().toISOString(),
        };
      });
    },
    onSuccess: async (response, request) => {
      client.invalidateQueries(requestQueryKey.self(request));
      // Same widget-staleness reason as approve.onSuccess above (#2447).
      client.invalidateQueries({ queryKey: [authQueries.requests] });
      client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
      invalidateParentDetail(request);
    },
  });

  const isAnyMutationLoading =
    complete.isLoading ||
    approve.isLoading ||
    requestReedit.isLoading ||
    undoReeditRequest.isLoading;

  return {
    approve,
    requestReedit,
    undoReeditRequest,
    complete,
    setQueryData,
    isAnyMutationLoading,
  };
};
